import { DAY_MS } from '@anm/constants/date';
import getDateMonthYear from '@anm/helpers/date/getDateMonthYear';
import getNextMonthDate from '@anm/helpers/date/getNextMonthDate';
import getPrevMonthDate from '@anm/helpers/date/getPrevMonthDate';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import noop from '@anm/helpers/noop';
import useOutsideClick from '@anm/hooks/useOutsideClick';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, useRef, useState, MouseEvent } from 'react';

import { LAST_24_HOURS, LAST_30_DAYS, LAST_3_DAYS, LAST_7_DAYS, LAST_MONTH, LIFETIME, THIS_MONTH } from '.';
import { createLabel, getDays, getEndDate } from './helpers';
import { ActionListProps, DateCellProps, DateContainerProps, DatePickerContainerProps } from './types';

const DROPDOWN_CLOSE_ANIMATION_TIME = 300;

export const useDates = (currentViewDate: number) => {
  const [dates, setDates] = useState<(DateCellProps | undefined)[]>();
  const newDates = getDays(currentViewDate);

  useEffect(() => {
    setDates(newDates);
  }, [currentViewDate]);

  return dates;
};

export const useDateCellHandlers = ({
  date,
  selectedDates,
  onDateChange,
  currentViewDate,
  disableDates
}: DateContainerProps) => {
  const startDateMs = selectedDates[0];
  const [date1, month1, year1] = getDateMonthYear(startDateMs);
  const startDateWithoutHoursMs = new Date(year1, month1, date1).getTime();

  const endDateMs = selectedDates[1];
  const [date2, month2, year2] = getDateMonthYear(endDateMs || startDateMs);
  const endDateWithoutHoursMs = new Date(year2, month2, date2).getTime();

  const isSelected = !!date && (date?.value === startDateWithoutHoursMs || date?.value === endDateWithoutHoursMs);
  const dateMs = date?.value;

  const isBetweenSelectedDates = dateMs && dateMs > startDateMs && dateMs < endDateMs;

  const color = isSelected ? 'var(--blue_100)' : isBetweenSelectedDates ? '#F5F7FA' : 'transparent';

  const now = Date.now();
  const [dateNow, monthNow, yearNow] = getDateMonthYear(now);

  const isDisabled =
    !date ||
    (date &&
      disableDates &&
      (disableDates === 'future'
        ? date.value! > new Date(yearNow, monthNow, dateNow).getTime()
        : date.value! < new Date(yearNow, monthNow, dateNow).getTime()));

  const handleDateClick = useCallback(() => {
    if (!date) return;

    const [, month, year] = getDateMonthYear(currentViewDate);
    const currentDateMs = new Date(year, month, date.label).getTime();

    onDateChange(currentDateMs);
  }, [onDateChange, date, currentViewDate]);

  const isEndDate = date?.value === endDateWithoutHoursMs;
  const isStartDate = !!selectedDates[1] && date?.value === startDateWithoutHoursMs;

  const isSelectedOneDay =
    (!!endDateMs &&
      !!startDateMs &&
      endDateMs - startDateMs < DAY_MS &&
      new Date(startDateMs).getDate() === new Date(endDateMs).getDate()) ||
    !endDateMs;

  return {
    color,
    isSelected,
    isDisabled,
    isEndDate,
    isStartDate,
    handleDateClick,
    isSelectedOneDay
  };
};

export const useDatePickerHandlers = ({
  defaultDates = [],
  isRangeDate,
  isPreFilledList,
  lifetimeStartTime,
  onDateApply
}: DatePickerContainerProps) => {
  const [isPickerOpen, openPicker, closePicker] = useSwitchState();
  const [isActionsOpen, openActions, closeActions] = useSwitchState();

  const [dates, setDates] = useState<number[]>(defaultDates);
  const [savedDates, setSavedDates] = useState<number[]>(defaultDates);

  const [customLabel, setCustomLabel] = useState(LAST_7_DAYS);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const inputLabel = isPickerOpen || !customLabel || !isPreFilledList ? createLabel(dates) : customLabel;

  useEffect(() => {
    if (!lifetimeStartTime || lifetimeStartTime !== defaultDates[0]) return;

    setDates(defaultDates);
    setCustomLabel(LIFETIME);
    setSavedDates(defaultDates);
  }, [defaultDates]);

  const handleApplyDates = useCallback(
    (newDates: number[] = dates, canSetLabel = true) => {
      const safeDates = newDates.length === 1 && isRangeDate ? [newDates[0], getEndDate(newDates[0])] : newDates;

      onDateApply(safeDates);
      setSavedDates(safeDates);
      closePicker();
      canSetLabel && setCustomLabel('');
    },
    [onDateApply, dates, isRangeDate]
  );

  const canSaveOnOutsideClick = (isPickerOpen || isActionsOpen) && !isMobileOrIPad();
  const onOutsideClick = canSaveOnOutsideClick ? () => handleApplyDates(dates, false) : noop;

  useOutsideClick(wrapperRef, () => onOutsideClick());

  const handleDatePick = useCallback(
    (date: number) => {
      if (dates.length === 2 || !isRangeDate) return setDates([date]);

      if (dates[0] > date) {
        return setDates(oldDates => [date, getEndDate(oldDates[0])]);
      }

      setDates(oldDates => [...oldDates.slice(0, 1), getEndDate(date)]);
    },
    [dates, isRangeDate]
  );

  const handleCloseDropdown = useCallback(() => {
    setTimeout(closePicker, DROPDOWN_CLOSE_ANIMATION_TIME);
  }, []);

  const handleOpenPicker = useCallback(() => {
    openPicker();
    closeActions();
  }, []);

  const actionClickHandler = useCallback((label: string, dates: number[]) => {
    handleApplyDates(dates, false);
    closeActions();
    setCustomLabel(label);
  }, []);

  const handleClickCancel = useCallback(() => {
    closePicker();
    closeActions();
    setDates(savedDates);
  }, [savedDates]);

  return [
    dates,
    inputLabel,
    wrapperRef,
    isPickerOpen,
    isActionsOpen,
    setDates,
    openActions,
    handleDatePick,
    handleApplyDates,
    handleOpenPicker,
    handleClickCancel,
    actionClickHandler,
    handleCloseDropdown
  ] as const;
};

export const useActionListHandlers = ({
  onSetDates,
  onOpenPicker,
  onActionClick,
  lifetimeStartTime
}: ActionListProps) => {
  const handleLast24HoursClick = useCallback(() => {
    const endDate = Date.now();
    const startDate = endDate - DAY_MS;
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LAST_24_HOURS, [startDate, endDate]);
  }, []);

  const handleLast3DaysClick = useCallback(() => {
    const endDate = Date.now();
    const startDate = endDate - DAY_MS * 3;
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LAST_3_DAYS, [startDate, endDate]);
  }, []);

  const handleLast7daysClick = useCallback(() => {
    const endDate = Date.now();
    const startDate = endDate - DAY_MS * 7;
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LAST_7_DAYS, [startDate, endDate]);
  }, []);

  const handleLast30daysClick = useCallback((event: Pick<MouseEvent, 'stopPropagation'>) => {
    event.stopPropagation();
    const endDate = Date.now();
    const [date, month, year] = getDateMonthYear(endDate);

    const startDate = new Date(year, month, date - 30).getTime();
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LAST_30_DAYS, [startDate, endDate]);
  }, []);

  const handleThisMonthClick = useCallback((event: Pick<MouseEvent, 'stopPropagation'>) => {
    event.stopPropagation();
    const endDate = Date.now();
    const [, month, year] = getDateMonthYear(endDate);

    const startDate = new Date(year, month, 1).getTime();
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(THIS_MONTH, [startDate, endDate]);
  }, []);

  const handleLastMonthClick = useCallback((event: Pick<MouseEvent, 'stopPropagation'>) => {
    event.stopPropagation();
    const [, month, year] = getDateMonthYear();

    const startDate = new Date(year, month - 1, 1).getTime();
    const endDate = new Date(year, month, 0).getTime();
    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LAST_MONTH, [startDate, endDate]);
  }, []);

  const handleLifetimeClick = useCallback(() => {
    const endDate = Date.now();
    const defaultStartTime = new Date(2010, 1, 1).getTime();
    const startDate = lifetimeStartTime || defaultStartTime;

    const dates = [startDate, endDate];

    onSetDates(dates);
    onActionClick(LIFETIME, [startDate, endDate]);
  }, [lifetimeStartTime]);

  const handleOpenPicker = useCallback((event: Pick<MouseEvent, 'stopPropagation'>) => {
    event.stopPropagation();
    onOpenPicker();
  }, []);

  return [
    handleOpenPicker,
    handleLast24HoursClick,
    handleLast3DaysClick,
    handleLast7daysClick,
    handleLast30daysClick,
    handleThisMonthClick,
    handleLastMonthClick,
    handleLifetimeClick
  ] as const;
};

export const useMonthSwitcher = (selectedDates: number[] = []) => {
  const defaultViewDate = selectedDates[0] || Date.now();
  const [currentViewDate, setCurrentViewDate] = useState<number>(defaultViewDate);

  // this is prevent closing dropdown
  const handleStopPropagation = useCallback((evt: Pick<MouseEvent, 'stopPropagation'>) => evt.stopPropagation(), []);

  const handleNextMonthClick = useCallback(() => {
    const nextDate = getNextMonthDate(currentViewDate);
    setCurrentViewDate(nextDate);
  }, [currentViewDate]);

  const handlePrevMonthClick = useCallback(() => {
    const prevDate = getPrevMonthDate(currentViewDate);
    setCurrentViewDate(prevDate);
  }, [currentViewDate]);

  return [currentViewDate, handleNextMonthClick, handlePrevMonthClick, handleStopPropagation] as const;
};
